import ClearIcon from '@mui/icons-material/Clear'
import DeleteIcon from '@mui/icons-material/Delete'
import SaveIcon from '@mui/icons-material/Save'
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  TextField,
} from '@mui/material'
import Typography from '@mui/material/Typography'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { message, Table, TableColumnsType } from 'antd'
import dayjs, { Dayjs } from 'dayjs'
import { useFormik } from 'formik'
import { useEffect, useState } from 'react'
import * as Yup from 'yup'

import CircularLoading from '../../../components/CircularProgress'
import ImgTag from '../../../components/ImgTag'
import SelectSearch, { IOptionSelect } from '../../../components/SelectSearch'
import ProductSelectSearch from '../../../components/SelectSearch/ProductSelectSearch'
import { ERRORS_MESSAGE_MAPPER } from '../../../constants/errors'
import useAxios, { axiosInstance } from '../../../hooks/useAxios'
import { IDistributionChannel } from '../../../types/distribution-channel'
import { IExpense, IExpenseProduct } from '../../../types/expense'
import { PaginationResponseInterface } from '../../../types/pagination'
import { ICollections, IProduct } from '../../../types/product'
import ChannelSelectSearch from '../../../components/SelectSearch/ChannelSelectSearch'
import { ONLY_DATE_FORMAT } from '../../../constants/global'

interface IExpenseFormModalProps {
  isOpen: boolean
  onClose: (visible: boolean) => void
  expense?: IExpense
  onUpdateExpensesSuccess: () => void
}

interface IExpenseFormModal {
  name: string
  amount: number
  costDate: Dayjs
  products: IExpenseProduct[]
  distributionObjectId: string
  note: string
  adsStatus: string
  isShipping: string
}

const getProductFormValidationSchema = () => {
  return Yup.object({
    name: Yup.string()
      .required('กรุณากรอกชื่อรายการค่าใช้จ่าย')
      .min(3, 'ชื่อรายการค่าใช้จ่ายต้องมีอย่างน้อย 3 ตัวอักษร'),
    amount: Yup.number()
      .required('กรุณากรอกราคาค่าใช้จ่าย')
      .min(1, 'ราคาค่าใช้จ่ายต้องมีอย่างน้อย 1 บาท'),
    products: Yup.array().required('กรุณาเลือกสินค้าอย่างน้อย 1 ชิ้น'),
    // distributionObjectId: Yup.string().required('กรุณาเลือกช่องทางการขาย'),
    // note: Yup.string()
    //   .required('กรุณากรอกรายละเอียด')
    //   .min(1, 'รายละเอียดต้องมีอย่างน้อย 1 ตัวอักษร'),
    costDate: Yup.date().required('กรุณาเลือกวันที่'),
  })
}

export const ExpenseFormModal = (props: IExpenseFormModalProps) => {
  const { isOpen, onClose, expense, onUpdateExpensesSuccess } = props
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [searchDistributionChannel, setSearchDistributionChannel] =
    useState<string>('')
  const [searchProduct, setSearchProduct] = useState<string>('')

  useEffect(() => {
    setIsLoading(true)
    if (expense) {
      // formik.setValues({
      //   name: expense?.name ?? '',
      //   amount: expense?.amount ?? 0,
      //   costDate: expense?.costDate ?? dayjs(Date.now()),
      //   products: expense?.products ?? [],
      //   distributionObjectId: expense?.distribution?.objectId ?? '',
      //   note: expense?.note ?? '',
      // } as IExpenseFormModal)
    }
    setIsLoading(false)
  }, [expense])

  const formik = useFormik<IExpenseFormModal>({
    initialValues: {
      name: '',
      amount: 0,
      costDate: dayjs(),
      products: [],
      distributionObjectId: '',
      note: '',
      adsStatus: '',
      isShipping: '2',
    } as IExpenseFormModal,
    validationSchema: getProductFormValidationSchema(),
    onSubmit: async (values: IExpenseFormModal) => {
      // if (values.products.length === 0) {
      //   message.error('กรุณาเลือกสินค้าอย่างน้อย 1 ชิ้น')
      //   return
      // }

      // if (!values.distributionObjectId) {
      //   message.error('กรุณาเลือกช่องทางการขาย')
      //   return
      // }

      if (!values.costDate) {
        message.error('กรุณาเลือกวันที่')
        return
      }

      setIsLoading(true)
      const isUpdate = !!expense
      try {
        const response = await axiosInstance.request({
          method: isUpdate ? 'PUT' : 'POST',
          url: isUpdate ? `/costs/${expense?.objectId}` : '/costs',
          data: {
            name: values.name,
            amount: Number(values.amount),
            date: values.costDate,
            products: values?.products?.map(
              (product: IExpenseProduct) => product.objectId,
            ),
            adsStatus: values.adsStatus,
            isShipping: values.isShipping === '1' ? true : false,
            // products: values.products.reduce((acc: string[], product: IExpenseProduct) => {
            //   return [...acc, product.objectId]
            // }, [] as string[]),
            distributionObjectId: values?.distributionObjectId ?? '',
            note: values.note,
          },
        })
        const { status } = response
        message.success(
          status === 201
            ? `สร้างรายการค้าใช้ ${values.name} สำเร็จ`
            : `แก้ไขรายการค่าใช้ ${values?.name} สำเร็จ`,
        )
        formik.resetForm()
        onClose(false)
        onUpdateExpensesSuccess()
      } catch (e: any) {
        const messageError =
          ERRORS_MESSAGE_MAPPER?.[e.response?.data?.message ?? ''] ||
          'บันทึกข้อมูลไม่สำเร็จ'
        message.error(messageError)
      }
      setIsLoading(false)
    },
  })

  const distributionChannelsList = useAxios<
    PaginationResponseInterface<IDistributionChannel>
  >({
    method: 'GET',
    url: '/distribution-channels',
    params: {
      page: 1,
      perPage: 5,
      name: searchDistributionChannel,
      startDate: '',
      endDate: '',
    },
  })

  const handleSearchDistributionChannel = async (value: string) => {
    setSearchDistributionChannel(value)
    await distributionChannelsList.mutate()
  }

  const productList = useAxios<PaginationResponseInterface<IProduct>>({
    method: 'GET',
    url: '/product',
    params: {
      page: 1,
      perPage: 5,
      search: searchProduct,
      startDate: '',
      endDate: '',
    },
  })

  const handleSearchProduct = async (value: string) => {
    setSearchProduct(value)
    await productList.mutate()
  }

  const handleDeleteProductByIndex = (index: number) => {
    formik.setFieldValue(
      'products',
      formik.values.products.filter((_, i) => i !== index),
    )
  }

  const columns: TableColumnsType<ICollections & { key: number }> = [
    {
      title: 'ลำดับ',
      dataIndex: 'numberRow',
      key: 'numberRow',
      align: 'center' as const,
      width: 80,
      render: (numberRow: number) => {
        return (
          <Box>
            <Typography
              variant="subtitle1"
              color="text.secondary"
              textAlign={'center'}
            >
              {numberRow}
            </Typography>
          </Box>
        )
      },
    },
    {
      title: 'รายการสินค้า',
      dataIndex: 'name',
      key: 'name',
      render: (name: string, product: ICollections) => {
        return (
          <Box
            sx={{
              minWidth: 200,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
              textAlign: 'left',
            }}
          >
            <ImgTag imageUrl={product.image} name={name} />
            <Box ml={1}>
              <Typography variant="h6" color="text.secondary">
                {name}
              </Typography>
              <Typography variant="subtitle2" color="text.secondary">
                รหัสสินค้า : {product?.code}
              </Typography>
            </Box>
          </Box>
        )
      },
    },
    {
      title: 'ราคา (บาท)',
      dataIndex: 'price',
      key: 'price',
      render: (price: number) => (
        <Box sx={{ minWidth: 60 }}>
          <Typography variant="subtitle2" color="text.secondary">
            {(price || 0).toLocaleString()}
          </Typography>
        </Box>
      ),
    },
    {
      dataIndex: 'key',
      key: 'key',
      width: 60,
      render: (key: number) => (
        <Box>
          <IconButton onClick={() => handleDeleteProductByIndex(key)}>
            <DeleteIcon />
          </IconButton>
        </Box>
      ),
    },
  ]

  return (
    <Dialog open={isOpen} onClose={() => onClose(false)} maxWidth="md">
      <CircularLoading isLoading={isLoading} />
      <DialogTitle>
        {expense ? 'แก้ไขรายการค่าใช้จ่าย' : 'เพิ่มรายการค่าใช้จ่าย'}
      </DialogTitle>
      <DialogContent>
        <Box component="form" mt={1} onSubmit={formik.handleSubmit}>
          <Grid container spacing={2} justifyContent="space-between">
            <Grid item xs={12} lg={12}>
              <TextField
                id="name"
                fullWidth
                label="ชื่อรายการค่าใช้จ่าย"
                name="name"
                helperText={
                  formik.touched.name && (formik.errors.name as string)
                }
                error={!!(formik.touched.name && formik.errors.name)}
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                value={formik.values.name}
              />
            </Grid>
            <Grid item xs={12} lg={4}>
              <ChannelSelectSearch
                multiple={false}
                label={'เลือกช่องทางการขาย'}
                defaultValue={
                  expense && {
                    label: expense?.distribution?.name as string,
                    value: expense?.distribution as IDistributionChannel,
                  }
                }
                helperText={
                  formik.touched.distributionObjectId &&
                  (formik.errors.distributionObjectId as string)
                }
                error={
                  !!(
                    formik.touched.distributionObjectId &&
                    formik.errors.distributionObjectId
                  )
                }
                onSelect={(e) => {
                  console.log(e)
                  formik.setFieldValue(
                    'distributionObjectId',
                    e?.value?.objectId,
                  )
                }}
              />
            </Grid>
            <Grid item xs={12} lg={4}>
              <TextField
                id="ads"
                fullWidth
                label="สถานะของการยิง Ads"
                name="adsStatus"
                helperText={
                  formik.touched.adsStatus &&
                  (formik.errors.adsStatus as string)
                }
                error={!!(formik.touched.adsStatus && formik.errors.adsStatus)}
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                value={formik.values.adsStatus}
                select
              >
                <MenuItem value="">ไม่ระบุ</MenuItem>
                <MenuItem value="active">ยิงติด</MenuItem>
                <MenuItem value="inactive">ยิงไม่ติด</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} lg={4}>
              <TextField
                id="shipping"
                fullWidth
                label="กำหนดให้เป็นค่าขนส่ง"
                name="isShipping"
                helperText={
                  formik.touched.isShipping &&
                  (formik.errors.isShipping as string)
                }
                error={
                  !!(formik.touched.isShipping && formik.errors.isShipping)
                }
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                value={formik.values.isShipping}
                select
              >
                <MenuItem value="2">ไม่ใช่</MenuItem>
                <MenuItem value="1">ใช่</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} lg={6}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  // disableFuture
                  format={ONLY_DATE_FORMAT}
                  label="วันที่"
                  name="costDate"
                  sx={{ width: '100%' }}
                  value={
                    formik.values.costDate
                      ? dayjs(formik.values.costDate)
                      : dayjs(Date.now())
                  }
                  onChange={(e) => formik.setFieldValue('costDate', e)}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} lg={6}>
              <TextField
                id="amount"
                fullWidth
                label="จำนวน (บาท)"
                name="amount"
                helperText={
                  formik.touched.amount && (formik.errors.amount as string)
                }
                error={!!(formik.touched.amount && formik.errors.amount)}
                onBlur={formik.handleBlur}
                type={'number'}
                onKeyPress={(e) => {
                  if (['+', '-', 'e', '*'].includes(e.key)) {
                    e.preventDefault()
                  }
                }}
                onChange={(e) => {
                  formik.setFieldValue('amount', e.target.value || undefined)
                }}
                value={formik.values.amount}
              />
            </Grid>
            <Grid item xs={12} lg={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                id="note"
                label="หมายเหตุ"
                name="note"
                helperText={
                  formik.touched.note && (formik.errors.note as string)
                }
                error={!!(formik.touched.note && formik.errors.note)}
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                value={formik.values.note}
              />
            </Grid>
            <Grid item xs={12} lg={12}>
              <ProductSelectSearch
                multiple={false}
                onSelect={(e) =>
                  e &&
                  formik.setFieldValue('products', [
                    ...formik.values.products,
                    e?.value,
                  ])
                }
                error={!!(formik.touched.products && formik.errors.products)}
                helperText={
                  formik.touched.products && (formik.errors.products as string)
                }
              />
            </Grid>
            {formik.values.products.length > 0 && (
              <Grid item xs={12} lg={12}>
                <Grid container>
                  <Grid item lg={12}>
                    <DialogTitle>รายการ เซตสินค้าที่เลือก</DialogTitle>
                  </Grid>
                  <Grid item xs={12} lg={12}>
                    <Table
                      columns={columns}
                      dataSource={formik.values.products?.map(
                        (product: ICollections, index: number) => {
                          return {
                            ...product,
                            key: index,
                            numberRow: index + 1,
                          }
                        },
                      )}
                      bordered
                      pagination={false}
                      summary={() => (
                        <>
                          <Table.Summary.Row>
                            <Table.Summary.Cell index={0} colSpan={1}>
                              <Typography
                                variant="subtitle2"
                                color="text.secondary"
                                textAlign={'center'}
                              >
                                รวม
                              </Typography>
                            </Table.Summary.Cell>
                            <Table.Summary.Cell index={1} colSpan={2}>
                              <Typography
                                variant="subtitle2"
                                color="text.secondary"
                                textAlign={'center'}
                              >
                                {formik.values.products
                                  .reduce(
                                    (acc: number, product: IExpenseProduct) =>
                                      acc + (product.price || 0),
                                    0,
                                  )
                                  .toLocaleString()}{' '}
                                บาท
                              </Typography>
                            </Table.Summary.Cell>
                          </Table.Summary.Row>
                        </>
                      )}
                    />
                  </Grid>
                </Grid>
              </Grid>
            )}
            <Grid container spacing={2} mt={2} justifyContent={'flex-end'}>
              <Grid item>
                <Button
                  startIcon={<SaveIcon />}
                  color="primary"
                  size="large"
                  variant="contained"
                  type="submit"
                >
                  {expense ? 'แก้ไขค่าใช้จ่าย' : 'เพิ่มค่าใช้จ่าย'}
                </Button>
              </Grid>
              <Grid item>
                <Button
                  color="inherit"
                  size="large"
                  variant="contained"
                  onClick={() => onClose(false)}
                  startIcon={<ClearIcon />}
                >
                  ปิดหน้าต่าง
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
    </Dialog>
  )
}
